import {
  Component,
  Input,
  OnChanges
} from '@angular/core';

import {
  RepairGroup,
  StoryLine,
  RepairsCard,
  RepairsBlock,
  TableContent
}                                     from '../typings/haynes-pro.diagnostic-tool.repair-details';
import { ImageViewerService }         from '../../shared/components/image-viewer/image-viewer.service';
import { HaynesProRepairManualModel } from '../typings/haynes-pro.repair-manual';

@Component({
  selector   : 'wor-haynes-pro-diagnostic-tool-repair-details',
  styles: [ require('./haynes-pro.diagnostic-tool.repair-details.component.scss') ],
  template: require('./haynes-pro.diagnostic-tool.repair-details.component.html')
})
export class HaynesProDiagnosticToolRepairDetailsComponent implements OnChanges {
  @Input() group        : string;
  @Input() maxHeight    = '70vh';
  @Input() manuals      : Array<HaynesProRepairManualModel>;
  @Input() story        : string;

  cards: Array<RepairsCard> = [];

  get groupLabel (): string {
    return this.group;
  }

  constructor(
    private imageViewerService: ImageViewerService
  ) {}

  ngOnChanges () : void {
    const groups: Array<RepairGroup> = this.getRepairsArray(this.manuals);

    this.cards = groups.map(g => ({
      title : g.name || '',
      blocks: this.flattenBlocks(g.sub_story_lines || [])
    }))
    .filter(c => c.title || c.blocks.length);
  }

  flattenBlocks ( lines: Array<StoryLine> ) : Array<RepairsBlock> {
    const out: RepairsBlock[] = [];

    for (const ln of (lines || [])) {
      this.addBlocksForLine(ln, out);
    }

    return out;
  }

  formatNote ( text : string ) : string {
    if (!text) {
      return '';
    }

    return text.replace(/^Note:/i, '<strong>Note:</strong>');
  }

  getRepairsArray ( input: any ): Array<RepairGroup> {
    if (Array.isArray(input)) {
      return input as RepairGroup[];
    }
    if (input?.repairs && Array.isArray(input.repairs)) {
      return input.repairs as RepairGroup[];
    }
    return [];
  }

  private addBlocksForLine ( ln: StoryLine, out: RepairsBlock[] ) : void {
    const trimmedName = (ln.name || '').trim();

    switch (ln.group_style) {
      case 'PARAGRAPH':
        this.handleParagraphLine(ln, out);
        break;

      case 'BULLET_LIST':
        this.handleBulletListLine(ln, out);
        break;

      case 'NUMBERED_LIST':
        this.handleNumberedListLine(ln, out);
        break;

      case 'TABLE':
        this.handleTableLine(ln, out);
        break;

      case '':
      default:
        this.handleDefaultLine(ln, trimmedName, out);
        break;
    }

    if (ln.group_style !== 'BULLET_LIST' && ln.group_style !== 'NUMBERED_LIST') {
      for (const child of (ln.sub_story_lines || [])) {
        this.addBlocksForLine(child, out);
      }
    }
  }

  private handleBulletListLine ( ln: StoryLine, out: RepairsBlock[] ) : void {
    let items = (ln.paragraph_content || [])
      .map(p => ({
        text: (p?.name || '').trim()
      }))
      .filter(item => item.text);

    if (!items.length) {
      items = (ln.sub_story_lines || [])
        .map(child => ({
          image: child?.image_url || undefined,
          text : (child?.name || '').trim()
        }))
        .filter(item => item.text);
    }

    if (!items.length) {
      return;
    }

    out.push({ kind: 'bullets', items });
  }

  private handleDefaultLine( ln: StoryLine, trimmedName: string, out: RepairsBlock[] ) : void {
    const img        = ln.image_url || undefined;
    const smartLinks = (ln as any).smart_links || [];

    //inline single smart_link value when it repeats the name
    let inlineValue = '';
    if (Array.isArray(smartLinks) && smartLinks.length === 1) {
      const texts = (smartLinks[0]?.text_items || [])
        .map((t: string) => (t || '').trim())
        .filter(Boolean);

      if (trimmedName && texts.length > 1 && texts[0] === trimmedName) {
        inlineValue = texts.slice(1).join(' ');
      }
    }

    //Special smart_links - (Torque-settings styled)
    const hasSpecialSmartLinkPattern =
      Array.isArray(smartLinks)
      && smartLinks.length > 1
      && !!trimmedName
      && !!(smartLinks[0]?.text_items
      && smartLinks[0].text_items.length)
      && (smartLinks[0].text_items[0] || '').trim().startsWith(trimmedName);

    if (hasSpecialSmartLinkPattern) {
      out.push({
        kind : 'headerline',
        text : trimmedName,
        style: ln.sentence_style
      });

      this.pushSmartLinksInOrder(smartLinks, trimmedName, out);

      if (img) {
        out.push({ kind: 'image', image: img });
      }

      return;
    }

    //Normal line with optional special_tools + remark
    const tools  = (ln as any).special_tools || [];
    const remark = ((ln as any).remark || '').toString().trim();

    const toolParts = tools
      .map((t: any) => {
        const desc = (t?.description || '').trim();
        const code = (t?.code || '').trim();

        if (code && desc && desc !== trimmedName) {
          return `${desc} ${code}`.trim();
        }
        if (code) {
          return code;
        }
        if (desc && desc !== trimmedName) {
          return desc;
        }
        return '';
      })
      .filter(Boolean);

    // use remark if present, otherwise fall back to inlineValue from smart_links
    const combinedText = [trimmedName, ...toolParts, remark || inlineValue]
      .filter(Boolean)
      .join(' ');

    if (combinedText) {
      if (ln.sentence_style === 'HEADER_STYLE') {
        out.push({
          kind : 'headerline',
          text : combinedText,
          style: ln.sentence_style
        });
      }
      else {
        out.push({
          kind : 'line',
          text : combinedText,
          style: ln.sentence_style
        });
      }
    }

    this.pushSmartLinksInOrder(smartLinks, trimmedName, out);

    if (img) {
      out.push({ kind: 'image', image: img });
    }
  }

  private handleNumberedListLine ( ln: StoryLine, out: RepairsBlock[] ) : void {
    let items = (ln.paragraph_content || [])
      .map(p => ({
        text: (p?.name || '').trim()
      }))
      .filter(item => item.text);

    if (!items.length) {
      items = (ln.sub_story_lines || [])
        .map(child => ({
          text: (child?.name || '').trim()
        }))
        .filter(item => item.text);
    }

    if (!items.length) {
      return;
    }

    out.push({ kind: 'numbers', items });
  }

  private handleParagraphLine ( ln: StoryLine, out: RepairsBlock[] ) : void {
    const paras = (ln.paragraph_content || [])
      .map(p => (p?.name || '').trim())
      .filter(Boolean);

    if (paras.length) {
      const combined = paras.join(' ');
      out.push({ kind: 'paragraph', paragraphs: [ combined ] });
    }

    const img = ln.image_url || undefined;
    if (img) {
      out.push({ kind: 'image', image: img });
    }

  }

  private handleTableLine ( ln: StoryLine, out: RepairsBlock[] ) : void {
    const tables = (ln.table_content || [])
      .map(t => this.tableToMatrix(t))
      .filter(t => t.length);

    for (const matrix of tables) {
      out.push({ kind: 'table', table: matrix });
    }

    const img = ln.image_url || undefined;
    if (img) {
      out.push({ kind: 'image', image: img });
    }
  }

  private mapSmartLinksToTableRows ( smartLinks : any[], trimmedName : string ) : string[][] {
    if (!Array.isArray(smartLinks) || !smartLinks.length) {
      return [];
    }

    const rows: string[][] = [];

    for (const sl of smartLinks) {
      const texts = (sl?.text_items || [])
        .map((t: string) => (t || '')
        .trim())
        .filter(Boolean);

      if (!texts.length) {
        continue;
      }

      if (trimmedName && texts[0] && trimmedName === texts[0] && texts.length === 1) {
        continue;
      }

      const label = texts[0];
      const value = texts.slice(1).join(' ');

      rows.push([label, value]);
    }

    return rows;
  }

  // Order the smartlinks based on the data
  private pushSmartLinksInOrder ( smartLinks : any[], trimmedName : string, out : RepairsBlock[] ) : void {
    if (!Array.isArray(smartLinks) || !smartLinks.length) {
      return;
    }

    const pending: any[] = [];

    const flush = () : void => {
      const rows = this.mapSmartLinksToTableRows(pending, trimmedName);
      if (rows.length) {
        out.push({
          kind: 'table',
          table: rows
        });
      }
      pending.length = 0;
    };

    for (const sl of smartLinks) {
      if (sl?.operation === 'IMAGE' && sl?.id2) {
        flush();
        out.push({ kind: 'image', image: sl.id2 as string });
        continue;
      }

      pending.push(sl);
    }

    flush();
  }


  private tableToMatrix ( t: TableContent | undefined ) : Array<Array<string>> {
    if (!t || !t.columns || !t.rows) {
      return [];
    }

    const cols = Number(t.columns);
    const rows = Number(t.rows);

    if (!cols || !rows) {
      return [];
    }

    const matrix: string[][] = Array.from({ length: rows }, () => Array(cols).fill(''));

    (t.table_data || []).forEach(cell => {
      const idx = Number(cell.order);

      if (Number.isFinite(idx)) {
        const r = Math.floor(idx / cols);
        const c = idx % cols;

        if (r >= 0 && r < rows && c >= 0 && c < cols) {
          matrix[r][c] = cell.value ?? '';
        }
      }
    });

    return matrix.filter(row => row.some(v => (v ?? '').toString().trim() !== ''));
  }

  viewImage ( src: string ) : void {
    this.imageViewerService.view(src);
  }
}